import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import Model from "@tripian/model";
import { BackButton, CardSlider, PreLoading } from "@tripian/react";
import TourApiProductCard from "./TourApiProductCard";
import useTourApiCategoryCatalogs from "../../hooks/useTourApiCategoryCatalogs";
import useTranslate from "../../hooks/useTranslate";
import {
  buildTourInfoSearch,
  isTourInfoSupported,
} from "../../utils/tourInfoNavigation";
import AppNav from "../../App/AppNav/AppNav";
import {
  LOCAL_EXPERIENCES,
  TOUR_INFO,
} from "../../constants/ROUTER_PATH_TITLE";

/**
 * Kategori satırları — sıra legacy sayfadaki `initialCatalog` ile aynı: Events en
 * sonda, çünkü legacy de Victory etkinliklerini diğer kategorilerden sonra ekliyordu.
 *
 * "Rezdy Tours" burada yok: o provider'a özel bir gruptu, tour-api ise ürünleri
 * provider'a göre değil kategoriye göre grupluyor — satır her zaman boş kalır ve
 * boş kategoriler gizlendiği için hiç görünmezdi.
 */
const CATEGORIES: { id: string; label: Model.TranslationKey }[] = [
  { id: "adventure", label: "trips.myTrips.localExperiences.categories.adventure" },
  { id: "food", label: "trips.myTrips.localExperiences.categories.food" },
  { id: "culture", label: "trips.myTrips.localExperiences.categories.cultureHistory" },
  { id: "sight", label: "trips.myTrips.localExperiences.categories.sightSeeing" },
  { id: "art", label: "trips.myTrips.localExperiences.categories.artMuseum" },
  { id: "local", label: "trips.myTrips.localExperiences.categories.localNeighborhood" },
  { id: "others", label: "trips.myTrips.localExperiences.categories.others" },
  { id: "events", label: "trips.myTrips.localExperiences.categories.events" },
];

const LocalExperiencesTourApiPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslate();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const cityId = query.get("city_id") ?? undefined;
  const cityName = query.get("city_name") ?? undefined;
  const lat = query.get("lat");
  const lng = query.get("lng");
  const startDate = query.get("start_date") ?? undefined;
  const endDate = query.get("end_date") ?? undefined;
  const adult = query.get("adult") ?? undefined;
  const children = query.get("children") ?? undefined;

  const categoryIds = useMemo(() => CATEGORIES.map(category => category.id), []);

  const providerId = useMemo(() => {
    const ids = window.tconfig.TOUR_TICKET_PROVIDER_IDS || [];
    return ids.length > 0 ? ids.join(",") : undefined;
  }, []);

  const { catalogs, loading } = useTourApiCategoryCatalogs({
    categoryIds,
    cityId,
    providerId,
    lat: lat ? Number(lat) : undefined,
    lng: lng ? Number(lng) : undefined,
    date: startDate,
    to: endDate,
    adults: adult ? Number(adult) : 1,
    currency: "usd",
    lang: window.twindow?.langCode || "en",
  });

  const header = LOCAL_EXPERIENCES.HEADER?.(
    t("trips.myTrips.localExperiences.title")
  );

  /** Legacy sayfadaki gibi arama bağlamını detay sayfasına taşır. */
  const tourInfoSearch = useMemo(
    () =>
      buildTourInfoSearch({
        cityId,
        cityName,
        startDate,
        endDate,
        adults: adult,
        children,
      }),
    [cityId, cityName, startDate, endDate, adult, children]
  );

  const openProduct = (product: Model.TourSearchProduct) => {
    const provider = product.providerId as Model.PROVIDER_ID;

    if (isTourInfoSupported(provider)) {
      navigate(
        `${TOUR_INFO.PATH}/${provider}/${product.productId}${tourInfoSearch}`,
        { state: { from: location.state?.from } }
      );
      return;
    }

    if (product.url) window.open(product.url, "_blank");
  };

  if (loading) {
    return (
      <>
        <AppNav header={header} />
        <PreLoading />
      </>
    );
  }

  const visibleCatalogs = catalogs.filter(catalog => catalog.items.length > 0);

  if (visibleCatalogs.length === 0) {
    return (
      <>
        <AppNav header={header} />
        <div className="mt-12 mx-auto p-4 text-center">
          {t("trips.myTrips.localExperiences.toursEmpty")}
        </div>
      </>
    );
  }

  return (
    <div className="px-8 pb-8 bg-background-color h-full overflow-y-auto">
      <AppNav header={header} />
      {window.history.length > 1 && (
        <div className="mt-4">
          <BackButton fill="#000" clicked={() => navigate(-1)} />
        </div>
      )}
      {visibleCatalogs.map(catalog => {
        const label = CATEGORIES.find(
          category => category.id === catalog.id
        )?.label;

        return (
          <div className="row m0" key={`toursCatalogGroup-${catalog.id}`}>
            <div className="col col12 p0 m0">
              <h3 className="m4 mt8 text-xl font-bold">
                {label ? t(label) : catalog.id}
              </h3>
              <div>
                <CardSlider>
                  {catalog.items.map((product, index) => (
                    <div
                      key={`${catalog.id}-${product.id}`}
                      onKeyPress={() => {}}
                      role="button"
                      tabIndex={index}
                    >
                      <TourApiProductCard
                        product={product}
                        onSelect={openProduct}
                        t={t}
                      />
                    </div>
                  ))}
                </CardSlider>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LocalExperiencesTourApiPage;
